/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Tables api map
 *
 * @author Yakov Gushchin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Maxim Fedorov
 * @author Evgenij Tyan
 * @author Julia Ignatieva
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */

import { SagaIterator } from 'redux-saga';
import { OutputSelector } from 'reselect';

import {
    ACCOUNT_CASHBACK_HISTORY,
    ACCOUNT_CASHBACK_HISTORY_LIST,
    API_AUTO_REPORT_ID_ACCOUNT_TABLE,
    API_AUTO_REPORT_ID_ACCOUNT_TABLE_BASKET_EDIT,
    API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_BASKET_EDIT,
    API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
    API_CAMPAIGN_INFO_ACCOUNT,
    API_CAMPAIGN_INFO_ACCOUNT_LIST,
    API_CAMPAIGN_INFO_BASKET,
    API_CAMPAIGN_INFO_SUBSCRIBER,
    API_CAMPAIGN_INFO_SUBSCRIBER_LIST,
    API_PAYMENTS,
    BILLS,
    BILLS_BASKET,
    EXPENSES_ANALYZER,
    EXPENSES_ANALYZER_BASKET,
    PERSONAL_ACCOUNT,
    PERSONAL_ACCOUNT_BASKET,
    PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET,
    SETTINGS_AUTO_REPORTS,
    SETTINGS_STATISTICS,
} from '../../../modules/app/Api';
import { IIntState } from '../../../modules/internationalization/intReducer';
import { makeSelectPageSettingsAutoReportsTableTranslation } from '../../../modules/internationalization/intSelectors';
import { ELogBusinessEventName } from '../../../modules/logging';
import {
    getAutoReportIdTableAccountColumnFilterType,
    getAutoReportIdTableSubscriberColumnFilterType,
} from '../../../pages/PageAutoReportId/components/Table/AutoReportIdTableCell';
import { getColumnFilterType as billsFilterType } from '../../../pages/PageBills/components/BillsTable/Columns/Cells';
import { MapBillsFieldToFooter } from '../../../pages/PageBills/pageBillsTypes';
import {
    getCampaignInfoTableAccountColumnFilterType,
    getCampaignInfoTableSubscriberColumnFilterType,
} from '../../../pages/PageCampaignInfo/components/Table/CampaignInfoTableCells';
import { getCashbackTableColumnFilterType } from '../../../pages/PageCashback/components/CashbackTabHistory/CashbackHistoryTableCells';
import { analyzerColumnFilterType } from '../../../pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/Columns/Cells';
import { MapExpensesAnalyzerFieldToFooter } from '../../../pages/PageExpensesAnalyzer/components/ExpensesAnalyzerTable/types/expensesAnalyzerTableTypes';
import { getTableAccountIdExpensesSaga } from '../../../pages/PageExpensesAnalyzer/pageExpensesAnalyzerSaga';
import { getColumnFilterType as labelsFilterType } from '../../../pages/PageLabelsImport/components/LabelsImportTable/LabelsImportTableCells';
import {
    getPagePaymentsTableColumnFilterType,
    MapPagePaymentFieldToFooter,
} from '../../../pages/PagePayments/const/PagePaymentsTable';
import { getColumnFilterType as accountFilterType } from '../../../pages/PagePersonalAccount/components/PersonalAccountTable/Columns/Cells';
import { getSettingsAutoReportsColumnFilterType as settingsAutoReportsColumnFilterType } from '../../../pages/PageSettings/components/SettingsAutoReports/components/SettingsAutoReportsTable/SettingsAutoReportsTableCells';
import { getSettingsStatisticsColumnFilterType } from '../../../pages/PageSettings/components/SettingsStatistics/components/SettingsStatisticsTable/settingsStatisticsTableCells';
import { getColumnFilterType as subscriberFilterType } from '../../../pages/PageSubscriberMobile/components/SubscriberMobileTable/Columns/Cells';
import { getColumnFilterType as importPersonalDataFilterType } from '../../../pages/WizardImportPersonalData/components/WizardImportPersonalDataTable/WizardImportPersonalDataTableCells';
import { getColumnFilterType as importSubscribersLimitsFilterType } from '../../../pages/WizardImportSubscribersLimits/components/WizardImportSubscribersLimitsTable/WizardImportSubscribersLimitsTableCells'
import { getColumnFilterType } from '../../../pages/WizardProductOfferings/components/ProductSelectSubscribersTable/ProductSelectSubscribersTableCells';
import { IRootReducer } from '../../../reducers/rootReducer';
import { TableDataType, TableNameType } from './tableTypes';

/**
 * @description Map table names to api path. Used in sagas
 * @see tableSagas: Table Saga iterator
 */
export const MapTableNameToApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: PERSONAL_ACCOUNT,
    [TableNameType.autoReportIdAccount]: API_AUTO_REPORT_ID_ACCOUNT_TABLE,
    [TableNameType.autoReportIdSubscriber]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
    [TableNameType.bills]: BILLS,
    [TableNameType.campaignInfoAccount]: API_CAMPAIGN_INFO_ACCOUNT,
    [TableNameType.campaignInfoSubscriber]: API_CAMPAIGN_INFO_SUBSCRIBER,
    [TableNameType.cashbackHistory]: ACCOUNT_CASHBACK_HISTORY,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: API_PAYMENTS,
    [TableNameType.productSelectSubscribers]: PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET,
    [TableNameType.settingsAutoReports]: SETTINGS_AUTO_REPORTS,
    [TableNameType.settingsStatistics]: SETTINGS_STATISTICS,
    [TableNameType.wizardBillDocAccount]: PERSONAL_ACCOUNT,
};

export const MapTableNameToLabelsApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: PERSONAL_ACCOUNT,
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
    [TableNameType.bills]: BILLS,
    [TableNameType.campaignInfoAccount]: API_CAMPAIGN_INFO_ACCOUNT,
    [TableNameType.campaignInfoSubscriber]: API_CAMPAIGN_INFO_SUBSCRIBER,
    [TableNameType.cashbackHistory]: ACCOUNT_CASHBACK_HISTORY,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: undefined,
    [TableNameType.productSelectSubscribers]: PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET,
    [TableNameType.settingsAutoReports]: SETTINGS_AUTO_REPORTS,
    [TableNameType.settingsStatistics]: SETTINGS_STATISTICS,
    [TableNameType.wizardBillDocAccount]: PERSONAL_ACCOUNT,
};

export const MapTableNameToListApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: PERSONAL_ACCOUNT,
    [TableNameType.autoReportIdAccount]: API_AUTO_REPORT_ID_ACCOUNT_TABLE,
    [TableNameType.autoReportIdSubscriber]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_EDIT,
    [TableNameType.bills]: BILLS,
    [TableNameType.campaignInfoAccount]: API_CAMPAIGN_INFO_ACCOUNT_LIST,
    [TableNameType.campaignInfoSubscriber]: API_CAMPAIGN_INFO_SUBSCRIBER_LIST,
    [TableNameType.cashbackHistory]: ACCOUNT_CASHBACK_HISTORY_LIST,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: API_PAYMENTS,
    [TableNameType.productSelectSubscribers]: undefined,
    [TableNameType.settingsAutoReports]: SETTINGS_AUTO_REPORTS,
    [TableNameType.settingsStatistics]: SETTINGS_STATISTICS,
    [TableNameType.wizardBillDocAccount]: PERSONAL_ACCOUNT,
};

export const MapTableNameToProxyApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: undefined,
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: undefined,
    [TableNameType.autoReportIdSubscriberEdit]: undefined,
    [TableNameType.bills]: undefined,
    [TableNameType.campaignInfoAccount]: undefined,
    [TableNameType.campaignInfoSubscriber]: undefined,
    [TableNameType.cashbackHistory]: undefined,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: undefined,
    [TableNameType.productSelectSubscribers]: undefined,
    [TableNameType.settingsAutoReports]: undefined,
    [TableNameType.settingsStatistics]: undefined,
    [TableNameType.wizardBillDocAccount]: undefined,
};

export const MapTableNameToBasketApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: PERSONAL_ACCOUNT_BASKET,
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: API_AUTO_REPORT_ID_ACCOUNT_TABLE_BASKET_EDIT,
    [TableNameType.autoReportIdSubscriberEdit]: API_AUTO_REPORT_ID_SUBSCRIBER_TABLE_BASKET_EDIT,
    [TableNameType.bills]: BILLS_BASKET,
    [TableNameType.campaignInfoAccount]: API_CAMPAIGN_INFO_BASKET,
    [TableNameType.campaignInfoSubscriber]: API_CAMPAIGN_INFO_BASKET,
    [TableNameType.cashbackHistory]: undefined,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER_BASKET,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: undefined,
    [TableNameType.productSelectSubscribers]: PRODUCT_OFFERINGS_SUBSCRIBERS_BASKET,
    [TableNameType.settingsAutoReports]: undefined,
    [TableNameType.settingsStatistics]: undefined,
    [TableNameType.wizardBillDocAccount]: PERSONAL_ACCOUNT_BASKET,
};

export const MapTableNameToDetailsApiPath: { [key in TableNameType]: string | undefined } = {
    [TableNameType.account]: undefined,
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: undefined,
    [TableNameType.autoReportIdSubscriberEdit]: undefined,
    [TableNameType.bills]: undefined,
    [TableNameType.campaignInfoAccount]: undefined,
    [TableNameType.campaignInfoSubscriber]: undefined,
    [TableNameType.cashbackHistory]: undefined,
    [TableNameType.expensesAnalyzer]: EXPENSES_ANALYZER,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: undefined,
    [TableNameType.productSelectSubscribers]: undefined,
    [TableNameType.settingsAutoReports]: undefined,
    [TableNameType.settingsStatistics]: undefined,
    [TableNameType.wizardBillDocAccount]: undefined,
};

export const MapTableNameToFilterType: { [key in TableNameType]: (name: string) => string } = {
    [TableNameType.account]: accountFilterType,
    [TableNameType.autoReportIdAccount]: getAutoReportIdTableAccountColumnFilterType,
    [TableNameType.autoReportIdSubscriber]: getAutoReportIdTableSubscriberColumnFilterType,
    [TableNameType.autoReportIdAccountEdit]: accountFilterType,
    [TableNameType.autoReportIdSubscriberEdit]: subscriberFilterType,
    [TableNameType.bills]: billsFilterType,
    [TableNameType.campaignInfoAccount]: getCampaignInfoTableAccountColumnFilterType,
    [TableNameType.campaignInfoSubscriber]: getCampaignInfoTableSubscriberColumnFilterType,
    [TableNameType.cashbackHistory]: getCashbackTableColumnFilterType,
    [TableNameType.expensesAnalyzer]: analyzerColumnFilterType,
    [TableNameType.importPersonalData]: importPersonalDataFilterType,
    [TableNameType.importSubscribersLimits]: importSubscribersLimitsFilterType,
    [TableNameType.labelsImport]: labelsFilterType,
    [TableNameType.payments]: getPagePaymentsTableColumnFilterType,
    [TableNameType.productSelectSubscribers]: getColumnFilterType,
    [TableNameType.settingsAutoReports]: settingsAutoReportsColumnFilterType,
    [TableNameType.settingsStatistics]: getSettingsStatisticsColumnFilterType,
    [TableNameType.wizardBillDocAccount]: accountFilterType,
};

export const MapTableNameToLoggerLoadList: { [key in TableNameType]: string | null } = {
    [TableNameType.account]: ELogBusinessEventName.B2B_ACCOUNTS,
    [TableNameType.autoReportIdAccount]: null,
    [TableNameType.autoReportIdSubscriber]: null,
    [TableNameType.autoReportIdAccountEdit]: null,
    [TableNameType.autoReportIdSubscriberEdit]: null,
    [TableNameType.bills]: ELogBusinessEventName.B2B_SHOW_BILLS,
    [TableNameType.campaignInfoAccount]: null,
    [TableNameType.campaignInfoSubscriber]: null,
    [TableNameType.cashbackHistory]: null,
    [TableNameType.expensesAnalyzer]: ELogBusinessEventName.B2B_SHOW_EXPENSES,
    [TableNameType.importPersonalData]: null,
    [TableNameType.importSubscribersLimits]: null,
    [TableNameType.labelsImport]: null,
    [TableNameType.payments]: null,
    [TableNameType.productSelectSubscribers]: null,
    [TableNameType.settingsAutoReports]: null,
    [TableNameType.settingsStatistics]: null,
    [TableNameType.wizardBillDocAccount]: ELogBusinessEventName.B2B_ACCOUNTS,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getIdFromTableName = (tableName: TableNameType, item: any): number => {
    switch (tableName) {
        case TableNameType.bills:
            return parseInt(item.billId);
        case TableNameType.expensesAnalyzer:
            return parseInt(item.subscriberId || item.bisAccountId);
        default:
            return parseInt(item.id);
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const MapColumnNameToFooterName: { [key in TableNameType]: any } = {
    [TableNameType.account]: undefined,
    [TableNameType.autoReportIdAccount]: undefined,
    [TableNameType.autoReportIdSubscriber]: undefined,
    [TableNameType.autoReportIdAccountEdit]: undefined,
    [TableNameType.autoReportIdSubscriberEdit]: undefined,
    [TableNameType.bills]: MapBillsFieldToFooter,
    [TableNameType.campaignInfoAccount]: undefined,
    [TableNameType.campaignInfoSubscriber]: undefined,
    [TableNameType.cashbackHistory]: undefined,
    [TableNameType.expensesAnalyzer]: MapExpensesAnalyzerFieldToFooter,
    [TableNameType.importPersonalData]: undefined,
    [TableNameType.importSubscribersLimits]: undefined,
    [TableNameType.labelsImport]: undefined,
    [TableNameType.payments]: MapPagePaymentFieldToFooter,
    [TableNameType.productSelectSubscribers]: undefined,
    [TableNameType.settingsAutoReports]: undefined,
    [TableNameType.settingsStatistics]: undefined,
    [TableNameType.wizardBillDocAccount]: undefined,
};

export const MapTableNameToGetAccountIdSaga: { [key in string]: () => SagaIterator } = {
    [TableNameType.expensesAnalyzer]: getTableAccountIdExpensesSaga,
};

export const MapTableNameToRemoveApi: { [key in TableNameType]?: string } = {
    [TableNameType.settingsAutoReports]: SETTINGS_AUTO_REPORTS,
};

export const MapTableNameToRemoveCallback: { [key in TableNameType]?: (row: TableDataType) => number } = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [TableNameType.settingsAutoReports]: (row: any) => row.cppoId ?? 0,
};

export interface ITableRemoveTranslation {
    modalRemoveMessage: string;
}

export const MapTableNameToRemoveRowTranslationSelector: {
    [key in TableNameType]?:
    () => OutputSelector<IRootReducer, ITableRemoveTranslation, (state: IIntState) => ITableRemoveTranslation>
} = {
    [TableNameType.settingsAutoReports]: makeSelectPageSettingsAutoReportsTableTranslation,
};
